// DJSEM Prompt 94 — DJoid-inspired Section Set Builder & Track Explorer model (pure logic).
//
// Platform-agnostic core behind the section-lane set builder UI (central lanes + right Track
// Explorer). Handles default + custom sections, moving tracks between lanes, and per-section
// roll-ups (count, duration, BPM/energy/danceability ranges, warnings). Deterministic + unit
// testable; the React lane/board layer is a thin renderer over this.

import { scoreTransition } from "./djoidGraphModel";
import type { DjoidTrackRow } from "./djoidLibraryModel";

/** The canonical default six lanes for a new set. */
export const DEFAULT_SECTIONS = ["Intro", "Warmup", "Build", "Peak", "Reset", "Closing"] as const;
/** One of the default section titles. */
export type DefaultSectionName = (typeof DEFAULT_SECTIONS)[number];

/** A section-builder lane: a titled bucket of ordered track ids. */
export interface SetSection {
  id: string;
  title: string;
  trackIds: string[];
}

/** Per-section roll-up: counts, duration, value ranges, and transition/content warnings. */
export interface SectionRollup {
  trackCount: number;
  durationSec: number;
  bpm: { min: number; max: number } | null;
  energy: { min: number; max: number } | null;
  danceability: { min: number; max: number } | null;
  warnings: string[];
}

/** Build the default six-lane section set (empty lanes). @returns The six {@link DEFAULT_SECTIONS} lanes, empty. */
export function createDefaultSections(): SetSection[] {
  return DEFAULT_SECTIONS.map((title, i) => ({ id: `sec_${i + 1}`, title, trackIds: [] }));
}

/**
 * Add a new, empty custom section.
 * @param sections - Current sections.
 * @param title - Title for the new section (falls back to a numbered default if blank).
 * @returns Sections with the new section appended.
 */
export function addSection(sections: SetSection[], title: string): SetSection[] {
  const id = `sec_${sections.length + 1}_${Date.now().toString(36)}`;
  return [...sections, { id, title: title.trim() || `Section ${sections.length + 1}`, trackIds: [] }];
}

/**
 * Rename a section.
 * @param sections - Current sections.
 * @param id - Id of the section to rename.
 * @param title - New title (falls back to the existing title if blank).
 * @returns Updated sections.
 */
export function renameSection(sections: SetSection[], id: string, title: string): SetSection[] {
  return sections.map((s) => (s.id === id ? { ...s, title: title.trim() || s.title } : s));
}

/**
 * Remove a section entirely (its tracks are dropped, not moved elsewhere).
 * @param sections - Current sections.
 * @param id - Id of the section to remove.
 * @returns Sections with the matching one removed.
 */
export function removeSection(sections: SetSection[], id: string): SetSection[] {
  return sections.filter((s) => s.id !== id);
}

/**
 * Add a track to a section (no duplicates within the same section).
 * @param sections - Current sections.
 * @param sectionId - Target section id.
 * @param trackId - Track id to add.
 * @returns Updated sections.
 */
export function addTrackToSection(sections: SetSection[], sectionId: string, trackId: string): SetSection[] {
  return sections.map((s) =>
    s.id === sectionId && !s.trackIds.includes(trackId)
      ? { ...s, trackIds: [...s.trackIds, trackId] }
      : s,
  );
}

/**
 * Move a track from wherever it is into a target section at an optional index.
 * @param sections - Current sections.
 * @param trackId - Track id to move.
 * @param toSectionId - Destination section id.
 * @param toIndex - Optional insertion index within the destination (appends if omitted).
 * @returns Updated sections with the track relocated.
 */
export function moveTrack(sections: SetSection[], trackId: string, toSectionId: string, toIndex?: number): SetSection[] {
  const stripped = sections.map((s) => ({ ...s, trackIds: s.trackIds.filter((t) => t !== trackId) }));
  return stripped.map((s) => {
    if (s.id !== toSectionId) return s;
    const next = [...s.trackIds];
    const at = toIndex == null ? next.length : Math.max(0, Math.min(next.length, toIndex));
    next.splice(at, 0, trackId);
    return { ...s, trackIds: next };
  });
}

/**
 * Remove a track from every section (used when a track is deleted from the set entirely).
 * @param sections - Current sections.
 * @param trackId - Track id to remove.
 * @returns Updated sections with the track removed from all lanes.
 */
export function removeTrackFromSet(sections: SetSection[], trackId: string): SetSection[] {
  return sections.map((s) => ({ ...s, trackIds: s.trackIds.filter((t) => t !== trackId) }));
}

function range(values: number[]): { min: number; max: number } | null {
  const nums = values.filter((v) => typeof v === "number" && !Number.isNaN(v));
  return nums.length ? { min: Math.min(...nums), max: Math.max(...nums) } : null;
}

/**
 * Per-section roll-up: counts, total duration, and value ranges + transition warnings.
 * @param section - Section to summarize.
 * @param byId - Track lookup by id.
 * @returns The computed {@link SectionRollup}.
 */
export function sectionRollup(section: SetSection, byId: Record<string, DjoidTrackRow>): SectionRollup {
  const tracks = section.trackIds.map((id) => byId[id]).filter(Boolean) as DjoidTrackRow[];
  const warnings: string[] = [];

  // Warn on risky adjacent transitions inside the lane.
  for (let i = 0; i < tracks.length - 1; i++) {
    const edge = scoreTransition(tracks[i], tracks[i + 1]);
    if (edge.risk === "risky") warnings.push(`risky: ${tracks[i].title} → ${tracks[i + 1].title}`);
  }
  // Warn on explicit tracks (useful for wedding/corporate section planning).
  const explicitCount = tracks.filter((t) => (t as { explicit?: boolean }).explicit).length;
  if (explicitCount) warnings.push(`${explicitCount} explicit track${explicitCount > 1 ? "s" : ""}`);

  return {
    trackCount: tracks.length,
    durationSec: tracks.reduce((sum, t) => sum + ((t as { durationSec?: number }).durationSec ?? 0), 0),
    bpm: range(tracks.map((t) => t.bpm ?? NaN)),
    energy: range(tracks.map((t) => t.energy ?? NaN)),
    danceability: range(tracks.map((t) => t.danceability ?? NaN)),
    warnings,
  };
}

/**
 * Whole-set roll-up (sum of sections) for the top bar / bottom player.
 * @param sections - All sections in the set.
 * @param byId - Track lookup by id.
 * @returns Total track count, duration, and warning count across all sections.
 */
export function setTotals(sections: SetSection[], byId: Record<string, DjoidTrackRow>): {
  trackCount: number; durationSec: number; warningCount: number;
} {
  return sections.reduce(
    (acc, s) => {
      const r = sectionRollup(s, byId);
      acc.trackCount += r.trackCount;
      acc.durationSec += r.durationSec;
      acc.warningCount += r.warnings.length;
      return acc;
    },
    { trackCount: 0, durationSec: 0, warningCount: 0 },
  );
}

/**
 * Track Explorer: tracks not yet placed in any section (candidates to add).
 * @param sections - Current sections.
 * @param all - All tracks available to the set.
 * @returns Tracks from `all` not present in any section's `trackIds`.
 */
export function unplacedTracks(sections: SetSection[], all: DjoidTrackRow[]): DjoidTrackRow[] {
  const placed = new Set(sections.flatMap((s) => s.trackIds));
  return all.filter((t) => !placed.has(t.id));
}
